// Feedback Module - TypeScript Interfaces

// ============ ENUMS ============
export type FeedbackCategory = 'Feature Request' | 'Change Request' | 'Removal Request' | 'Bug Report' | 'General'
export type FeedbackPriority = 'Low' | 'Medium' | 'High'
export type FeedbackStatus = 'Open' | 'In Progress' | 'Resolved' | 'Closed' | 'Wont Fix'

// ============ TICKET ============
export interface FeedbackTicket {
  id: string
  ticketNumber: number | null
  feedback: string
  tldr: string | null
  userStory: string | null
  acceptanceCriteria: string[]
  category: FeedbackCategory
  priority: FeedbackPriority
  status: FeedbackStatus
  affectedArea: string | null
  url: string
  pathname: string
  adminNotes: string | null
  aiGenerated: boolean
  screenshotContext: string | null
  submittedByEmail: string | null
  submittedByName: string | null
  submittedById: string | null
  module: string | null
  /** Workflow stage tracked alongside status (Submitted, Awaiting Developer Review,
   *  Awaiting Tester Verification, Resolved Pending Verification, Verified,
   *  Already Fixed, Won't Fix, Duplicate, Needs Data, Resolved).
   *  Set per migration 0024; nullable for pre-backfill rows.
   *  Used by the kanban view to group by workflow stage. */
  pipelineState: string | null
  reopenCount: number
  unreadByAdmin: number
  unreadByUser: number
  threadMessageCount: number
  screenshotCount: number
  externalLinks: FeedbackExternalLink[]
  createdAt: string
  updatedAt: string

  // ── Feedback Desk (2026-09-02): resolution, verification, last remark ──
  /** When the ticket last entered Resolved / Closed / Won't Fix. */
  resolvedAt: string | null
  resolvedByName: string | null
  /** The remark written at resolve time — required by the API to close. */
  resolutionNote: string | null
  fixRef: string | null
  deployEnv: string | null
  /** Reporter sign-off that the fix works. */
  verifiedAt: string | null
  verifiedByName: string | null
  lastReopenedAt: string | null
  lastReopenReason: string | null
  /** The most recent thing said about the ticket, whatever its source. */
  lastRemark: FeedbackRemark | null
  /** Attachments for the row, so thumbnails need no second request. */
  screenshots: FeedbackScreenshotRef[]

  // ── Super-admin desk (2026-09-02): tenant of the row. Only the admin
  //    desk endpoint fills the names; the tenant list never sends them.
  companyId?: string | null
  workspaceId?: string | null
  companyName?: string | null
  workspaceName?: string | null
  // Super-admin desk (finishing batch item 3): the console admin working the
  // ticket, and their name. Only the admin desk endpoint sends them.
  assigneeSuperAdminId?: string | null
  assigneeName?: string | null
}

export type FeedbackRemarkKind = 'reopen' | 'resolve' | 'thread' | 'note'

export interface FeedbackRemark {
  kind: FeedbackRemarkKind
  by: string
  at: string
  text: string
}

export interface FeedbackScreenshotRef {
  id: string
  type: string
  fileUrl: string | null
}

export type FeedbackDeployEnv = 'staging' | 'production' | 'none'

// ── Feedback Desk quick filters ──
export type FeedbackWindowKey = 'today' | 'yesterday' | '7d' | '30d' | 'all'
export type FeedbackLensKey = 'resolved' | 'raised' | 'verify' | 'reopened' | 'open'
export type FeedbackDeskView = 'digest' | 'list' | 'board'

export interface FeedbackDigestQuery {
  from?: string
  to?: string
  tz?: string
  days?: number
  search?: string
  category?: string[]
  priority?: string[]
  module?: string[]
  submittedById?: string[]
  /** The calendar pickers — so the counts describe the same set as the list. */
  createdFrom?: string
  createdTo?: string
  updatedFrom?: string
  updatedTo?: string
}

/** `GET /v1/feedback/digest` — every number computed in SQL. */
export interface FeedbackDigest {
  window: { from: string | null; to: string | null }
  raised: number
  resolved: number
  wontFix: number
  verified: number
  reopened: number
  awaiting: number
  lens: Record<FeedbackLensKey, number>
  series: Array<{ day: string; raised: number; resolved: number }>
}

/** Body of a resolve / status change from the page or the widget. */
export interface FeedbackResolvePayload {
  status: FeedbackStatus
  resolutionNote?: string
  fixRef?: string
  deployEnv?: FeedbackDeployEnv
  reopenReason?: string
}

// ============ THREAD MESSAGE ============
export interface FeedbackThreadMessage {
  id: string
  ticketId: string
  sender: 'Admin' | 'User'
  senderId: string | null
  message: string
  read: boolean
  createdAt: string
}

// ============ EXTERNAL LINK ============
export interface FeedbackExternalLink {
  id: string
  provider: string
  externalId: string
  externalUrl: string
  pushedAt: string
}

// ============ STATS ============
export interface FeedbackStats {
  total: number
  open: number
  inProgress: number
  resolved: number
  closed: number
  wontFix: number
  highPriority: number
  byCategory: Record<string, number>
  byPriority: Record<string, number>
}

// ============ FILTER ============
export interface FeedbackFilter {
  page?: number
  limit?: number
  search?: string
  status?: FeedbackStatus
  category?: FeedbackCategory
  priority?: FeedbackPriority
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
}

// ============ LIST QUERY (table — multi-select + date-range) ============
// Mirrors the `GET /v1/feedback` contract: multi-select facets are sent as
// comma-separated strings, date ranges as ISO strings.
export interface FeedbackListQuery {
  page: number
  limit: number
  sortBy:
    | 'ticketNumber'
    | 'createdAt'
    | 'updatedAt'
    | 'resolvedAt'
    | 'lastReopenedAt'
    | 'status'
    | 'priority'
    | 'category'
  sortOrder: 'ASC' | 'DESC'
  search?: string
  status?: string[]
  category?: string[]
  priority?: string[]
  module?: string[]
  submittedById?: string[]
  createdFrom?: string
  createdTo?: string
  updatedFrom?: string
  updatedTo?: string
  // Feedback Desk window filters — see lib/feedback/desk.ts for the lens map.
  resolvedFrom?: string
  resolvedTo?: string
  reopenedFrom?: string
  reopenedTo?: string
  verifiedFrom?: string
  verifiedTo?: string
  verification?: 'awaiting' | 'verified'
}

// ============ PAGINATED LIST ENVELOPE ============
export interface FeedbackListResult {
  data: FeedbackTicket[]
  total: number
  page: number
  limit: number
  totalPages: number
  hasNext: boolean
  hasPrevious: boolean
}

// ============ SUBMITTER (for "Submitted by" filter) ============
export interface FeedbackSubmitter {
  id: string
  name: string | null
  email: string | null
}

// ============ LIFECYCLE TIMELINE ============
export type FeedbackTimelineEventType =
  | 'submitted'
  | 'in_progress'
  | 'fix_deployed'
  | 'reopened'
  | 'resolved'
  | 'closed'
  | 'wont_fix'
  | 'verified'

export interface FeedbackTimelineEvent {
  id: string
  at: string
  type: FeedbackTimelineEventType
  title: string
  description: string | null
  actor: string | null
}

export interface FeedbackTimeline {
  ticketId: string
  ticketNumber: number | null
  status: FeedbackStatus
  reopenCount: number
  events: FeedbackTimelineEvent[]
}

// ============ SCREENSHOT ============
export interface FeedbackScreenshot {
  id: string
  type?: string
  fileKey?: string
  fileUrl?: string | null
}

// ============ STATUS HISTORY ============
export interface FeedbackStatusHistoryEntry {
  id: string
  fromStatus: string | null
  toStatus: string
  actorName: string | null
  actorEmail: string | null
  at: string
  /** What was said with the transition (resolution note / reopen reason). */
  note?: string | null
}

// ============ TICKET DETAIL (GET /v1/feedback/:id) ============
export interface FeedbackTicketDetail extends Omit<FeedbackTicket, 'screenshots'> {
  threadMessages: FeedbackThreadMessage[]
  /**
   * Attachments as assembled by `useFeedbackTicketDetail`, which fetches them
   * from `GET /feedback/:id/screenshots` alongside the ticket.
   *
   * NOT part of the `GET /feedback/:id` response — that route returns
   * `screenshotCount`, matching its declared DTO. It used to spread the whole
   * relation (each row carrying a `fileUrl`) because nothing serialised the
   * controller's return value; the backend now matches its contract.
   */
  screenshots: FeedbackScreenshot[]
  statusHistory: FeedbackStatusHistoryEntry[]
}

/** Exactly what `GET /feedback/:id` returns — attachments as a count only. */
export type FeedbackTicketDetailResponse = Omit<
  FeedbackTicketDetail,
  'screenshots'
> & { screenshotCount: number }

// ============ QUICK SUMMARY ============
/**
 * One similar-ticket hit returned by the AI summarize endpoint.
 * The BE returns full objects here — NOT bare id strings.
 */
export interface FeedbackSimilarTicket {
  id: string
  tldr: string | null
  category: string
  status: string
  priority: string
  similarity: number
  createdAt: string
}

export interface FeedbackQuickSummary {
  type: string
  tldr: string
  userStory: string | null
  acceptanceCriteria: string[]
  category: string
  priority: string
  affectedArea: string | null
  similarTickets: FeedbackSimilarTicket[]
  ticketCount: number
  ticketIds: string[]
}

// ============ INTEGRATION ============
export interface FeedbackIntegration {
  provider: string
  enabled: boolean
  projectId: string | null
  projectName: string | null
  connectedAt: string | null
}

// ============ DISPLAY HELPERS ============
export const FEEDBACK_STATUS_COLORS: Record<FeedbackStatus, string> = {
  Open: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  'In Progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  Resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  Closed: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  'Wont Fix': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

export const FEEDBACK_PRIORITY_COLORS: Record<FeedbackPriority, string> = {
  Low: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  Medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  High: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

export const FEEDBACK_CATEGORY_COLORS: Record<FeedbackCategory, string> = {
  'Feature Request': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  'Bug Report': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  'Change Request': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  'Removal Request': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  General: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
}

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  Open: 'Open',
  'In Progress': 'In Progress',
  Resolved: 'Resolved',
  Closed: 'Closed',
  'Wont Fix': "Won't Fix",
}

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  'Feature Request': 'Feature Request',
  'Bug Report': 'Bug Report',
  'Change Request': 'Change Request',
  'Removal Request': 'Removal Request',
  General: 'General',
}

export const FEEDBACK_CATEGORY_SUFFIXES: Record<FeedbackCategory, string> = {
  'Bug Report': 'BUG',
  'Feature Request': 'NFR',
  'Change Request': 'CR',
  'Removal Request': 'RR',
  General: 'GEN',
}

export function getTicketDisplayId(ticketNumber: number | null, category: FeedbackCategory): string {
  if (ticketNumber == null) return '-'
  const suffix = FEEDBACK_CATEGORY_SUFFIXES[category] ?? 'GEN'
  return `${ticketNumber}-${suffix}`
}
